import React from "react";
import { useTranslation } from "react-i18next";
import { TrashIcon, Tooltip, IconButton, Body2, ConfirmDialog } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { Can } from "~/components/Can/Can";
import { useSecurity } from "~/components/Hooks";
import { billableStatusLabels } from "~/utils/tracking.jsx";
import { ExpenseAttachmentPicker } from "./ExpenseAttachmentPicker.js";
import { ExpenseValue } from "./ExpensesValue.js";
import { BillableStatusPicker } from "../BillableStatus/BillableStatusPicker.js";
import { OptionPicker } from "../OptionPicker";
import { EditableSummary } from "../ProjectTime/Timelist/EditableSummary.js";
import { UserPicker } from "../ProjectTime/Timelist/UserPicker.js";
import { SelectExpenseDate } from "../SelectDate/SelectExpenseDate.js";

export const ExpenseListItem = ({
  expense,
  onMoveToTrash,
  canEdit,
  expenseCategories,
  onChange,
  trashConfirmation,
  onConfirm,
  onCancel,
  className,
}) => {
  const { t } = useTranslation();
  const can = useSecurity();
  const canChangeBillable = can(["change_billable_status"], expense);
  const canMarkAsPaid = can(["mark_as_paid"], expense);
  const canReassign = can(["reassign"], expense);

  return (
    <div className={classNames("tw-flex tw-h-10 tw-items-center tw-px-2 ac-border-radius-8", className)}>
      <div className="tw-w-20 tw-mr-2 tw-flex tw-shrink-0">
        <ExpenseValue
          id={expense.id}
          value={expense.value}
          onChange={onChange}
          canEdit={canEdit}
        />
      </div>
      <div className="tw-flex tw-w-[120px] tw-mr-2 tw-shrink-0">
        {canEdit && expenseCategories.length > 1 ? (
          <OptionPicker
            options={expenseCategories}
            selected={expense.category_id}
            onChange={onChange}
            className="tw-truncate tw-max-w-full"
            optionKey="category_id"
          />
        ) : (
          <Body2
            color="secondary"
            weight="medium"
            className="tw-truncate tw-px-2"
          >
            {expenseCategories[0]?.name}
          </Body2>
        )}
      </div>
      <div className="tw-mr-2 tw-shrink-0 tw-flex tw-w-8">
        <UserPicker
          projectId={expense.project_id}
          userId={expense.user_id}
          userName={expense.user_name}
          userEmail={expense.user_email}
          onChange={onChange}
          position="bottom-end"
          hasPriviledges={canReassign}
          placeholder={t("Choose a User")}
        />
      </div>
      <div className="tw-mr-4 tw-shrink-0 tw-flex tw-items-center tw-w-8">
        <ExpenseAttachmentPicker
          expenseId={expense.id}
          canEdit={canEdit}
          projectId={expense.project_id}
          attachmentsCount={Number(expense.attachments_count)}
          onDeleteAttachment={onChange}
        />
      </div>
      <div className="tw-hidden md:tw-flex tw-min-w-[132px] tw-max-w-xs tw-flex-1 tw-mr-2">
        <EditableSummary
          className="tw-w-full"
          summary={expense.summary}
          onChange={onChange}
          disabled={!canEdit}
        />
      </div>
      <Can
        I="see_billable_status"
        entity={expense}
      >
        <div className="tw-flex tw-w-[140px] tw-mr-2 tw-shrink-0">
          {canChangeBillable ? (
            <BillableStatusPicker
              selected={expense.billable_status}
              onChange={onChange}
              canMarkAsPaid={canMarkAsPaid}
            />
          ) : (
            <Body2
              color="secondary"
              weight="medium"
              className="tw-truncate tw-px-2"
            >
              {billableStatusLabels[expense.billable_status]}
            </Body2>
          )}
        </div>
      </Can>
      <div className="tw-flex tw-shrink-0 tw-ml-auto">
        {canEdit ? (
          <div className="tw-flex">
            <SelectExpenseDate
              expenseId={expense.id}
              onChange={onChange}
              projectId={expense.project_id}
            />
            <Tooltip title={t("Move to Trash")}>
              <IconButton
                variant="text gray"
                onClick={onMoveToTrash}
                className="tw-ml-2"
              >
                <TrashIcon />
              </IconButton>
            </Tooltip>
            <ConfirmDialog
              open={trashConfirmation}
              dialogTitle={t("Delete expense?")}
              dialogContent={t("The expense will remain in Trash for 30 days.")}
              confirmBtnText={t("Delete")}
              cancelBtnText={t("Cancel")}
              onConfirm={onConfirm}
              onCancel={onCancel}
            />
          </div>
        ) : (
          <div
            className="tw-shrink-0"
            style={{ width: "72px" }}
          >
            &nbsp;
          </div>
        )}
      </div>
    </div>
  );
};

ExpenseListItem.propTypes = {
  expense: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  onMoveToTrash: PropTypes.func,
  onChangeBillableStatus: PropTypes.func,
  onChangeJobType: PropTypes.func,
  onEdit: PropTypes.func,
  expenseCategories: PropTypes.array,
  onChangeUser: PropTypes.func,
  onChangeSummary: PropTypes.func,
  onChangeRecordDate: PropTypes.func,
  trashConfirmation: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

ExpenseListItem.defaultProps = {
  canEdit: false,
  expenseCategories: [],
  users: [],
};
